import { isIpv4 } from '@/function/begateway/rule.subject';
import type { TypeAccessRule } from '@/types/begateway/types.begateway.access';

type TypeRuleLike = Pick<TypeAccessRule, 'id' | 'subject' | 'scope' | 'action' | 'lifecycle' | 'state' | 'audit'>;

const toNumber = (ip: string): number => ip.split('.').reduce((acc, part) => (acc << 8) + Number(part), 0) >>> 0;

export const inCidr = (ip: string, cidr: string): boolean => {
    const [base, bits] = cidr.split('/');
    if (!base || bits === undefined || !isIpv4(ip) || !isIpv4(base)) return false;
    const mask = bits === '0' ? 0 : (~0 << (32 - Number(bits))) >>> 0;
    return (toNumber(ip) & mask) === (toNumber(base) & mask);
};

export const isLive = (rule: Pick<TypeAccessRule, 'state' | 'lifecycle'>, now = Date.now()): boolean =>
    rule.state === 'active' && rule.lifecycle.active && !(rule.lifecycle.expiresAt && Date.parse(rule.lifecycle.expiresAt) <= now);

export function subjectCovers(outer: Pick<TypeAccessRule, 'subject'>, inner: Pick<TypeAccessRule, 'subject'>): boolean {
    if (outer.subject.type === 'any') return true;
    if (outer.subject.type === inner.subject.type && outer.subject.value === inner.subject.value) return true;
    if (outer.subject.type === 'cidr' && inner.subject.type === 'ip') return inCidr(inner.subject.value, outer.subject.value);
    if (outer.subject.type === 'cidr' && inner.subject.type === 'cidr') {
        const [innerBase, innerBits] = inner.subject.value.split('/');
        const [, outerBits] = outer.subject.value.split('/');
        return Number(outerBits) <= Number(innerBits) && inCidr(innerBase, outer.subject.value);
    }
    return false;
}

export function scopeCovers(outer: Pick<TypeAccessRule, 'scope'>, inner: Pick<TypeAccessRule, 'scope'>): boolean {
    if (outer.scope.kind === 'global') return true;
    if (outer.scope.kind === 'route') return inner.scope.kind !== 'global' && outer.scope.route === inner.scope.route;
    return inner.scope.kind === 'endpoint' && outer.scope.endpointId === inner.scope.endpointId;
}

export function ruleOverriddenBy<T extends TypeRuleLike>(rules: T[], target: TypeRuleLike, now = Date.now()): T | null {
    if (target.action !== 'block' || !isLive(target, now)) return null;
    return rules.find((other) => other.id !== target.id
        && other.action === 'allow'
        && other.subject.type !== 'device'
        && isLive(other, now)
        && subjectCovers(other, target)
        && scopeCovers(other, target)) ?? null;
}

const expiresNoEarlier = (outer: TypeRuleLike, inner: TypeRuleLike): boolean =>
    !outer.lifecycle.expiresAt || Boolean(inner.lifecycle.expiresAt && Date.parse(outer.lifecycle.expiresAt) >= Date.parse(inner.lifecycle.expiresAt));

const sameReach = (a: TypeRuleLike, b: TypeRuleLike): boolean =>
    subjectCovers(a, b) && subjectCovers(b, a) && scopeCovers(a, b) && scopeCovers(b, a);

const isOlder = (a: TypeRuleLike, b: TypeRuleLike): boolean =>
    a.audit.createdAt < b.audit.createdAt || (a.audit.createdAt === b.audit.createdAt && a.id < b.id);

export function ruleRedundantWith<T extends TypeRuleLike>(rules: T[], target: TypeRuleLike, now = Date.now()): T | null {
    if (!isLive(target, now)) return null;
    return rules.find((other) => other.id !== target.id
        && other.action === target.action
        && isLive(other, now)
        && subjectCovers(other, target)
        && scopeCovers(other, target)
        && expiresNoEarlier(other, target)
        && (!sameReach(other, target) || isOlder(other, target))) ?? null;
}
